/*
 * Sudoku engine, part 1 of 3 — the board itself: geometry, text formats, the
 * exhaustive solver that proves "exactly one solution".
 *
 *   SudokuEngine.geometry(n)            rows, columns, boxes and peers of an n×n board (4, 6, 9)
 *   SudokuEngine.parse(text)            a pasted puzzle in any common notation → values
 *   SudokuEngine.format(values)         values → one line, '.' for an empty cell
 *   SudokuEngine.conflicts(geo, values) cells that break the rule right now
 *   SudokuEngine.solve(geo, values, o)  0, 1 or "more than one" solutions, the first two kept
 *
 * A classic script with no DOM on purpose: the page, the worker and the node
 * tests load this very file (the same reason as GameKit). Digits live in bit
 * masks — bit d-1 stands for digit d — so a cell's candidates are one integer.
 * Why the engine is shaped this way — docs/why/sudoku.md.
 */
(function (root) {
    'use strict';

    var S = root.SudokuEngine || (root.SudokuEngine = {});

    // Number of set bits and the lowest digit of every 9-bit mask.
    var POP = new Uint8Array(512), LOW = new Uint8Array(512);
    (function () {
        for (var m = 1; m < 512; m++) {
            POP[m] = POP[m >> 1] + (m & 1);
            var d = 1;
            while (!(m & (1 << (d - 1)))) d++;
            LOW[m] = d;
        }
    })();

    function digitsOf(mask) {
        var out = [];
        while (mask) {
            var d = LOW[mask];
            out.push(d);
            mask &= mask - 1;
        }
        return out;
    }

    // Box shape per board size: rows × columns of one box.
    var BOX = { 4: [2, 2], 6: [2, 3], 9: [3, 3] };
    var GEO = {};

    function geometry(n) {
        n = n | 0;
        if (GEO[n]) return GEO[n];
        var dims = BOX[n];
        if (!dims) throw new Error('SudokuEngine: unsupported size ' + n);
        var bh = dims[0], bw = dims[1], cells = n * n, perBand = n / bw;
        var rows = [], cols = [], boxes = [], i, r, c, b, u;
        for (i = 0; i < n; i++) { rows.push([]); cols.push([]); boxes.push([]); }
        var cellRow = new Uint8Array(cells), cellCol = new Uint8Array(cells), cellBox = new Uint8Array(cells);
        for (i = 0; i < cells; i++) {
            r = (i / n) | 0; c = i % n;
            b = ((r / bh) | 0) * perBand + ((c / bw) | 0);
            cellRow[i] = r; cellCol[i] = c; cellBox[i] = b;
            rows[r].push(i); cols[c].push(i); boxes[b].push(i);
        }
        // Units are numbered rows 0…n-1, columns n…2n-1, boxes 2n…3n-1.
        var units = rows.concat(cols, boxes);
        var sees = new Uint8Array(cells * cells), peers = [];
        for (i = 0; i < cells; i++) {
            var list = [], seen = {};
            var own = [units[cellRow[i]], units[n + cellCol[i]], units[2 * n + cellBox[i]]];
            for (u = 0; u < 3; u++) {
                for (var k = 0; k < n; k++) {
                    var j = own[u][k];
                    if (j !== i && !seen[j]) { seen[j] = 1; list.push(j); sees[i * cells + j] = 1; }
                }
            }
            list.sort(function (x, y) { return x - y; });
            peers.push(list);
        }
        GEO[n] = {
            n: n, bh: bh, bw: bw, cells: cells, full: (1 << n) - 1,
            rows: rows, cols: cols, boxes: boxes, units: units,
            cellRow: cellRow, cellCol: cellCol, cellBox: cellBox,
            peers: peers, sees: sees
        };
        return GEO[n];
    }

    function unitKind(geo, u) { return u < geo.n ? 'row' : (u < 2 * geo.n ? 'col' : 'box'); }
    function unitIndex(geo, u) { return u % geo.n; }
    function sees(geo, a, b) { return geo.sees[a * geo.cells + b] === 1; }

    // ── Text ─────────────────────────────────────────────────────────────────
    // What people actually paste: one line of 81 characters, a 9-line grid, a
    // grid drawn with |, + and dashes, comma-separated cells, or a link that
    // carries the board in a `bd=` parameter (SudokuWiki, SudokuExchange).
    // Empty cells arrive as 0 . _ * x ? or a dash.
    var SIZE_BY_COUNT = { 16: 4, 36: 6, 81: 9 };

    function parse(text) {
        text = String(text == null ? '' : text);
        var inLink = /[?&#]bd=([0-9.]{16,})/.exec(text);
        if (inLink) text = inLink[1];
        var lines = text.split(/\r\n|\r|\n/), kept = [], i;
        var dashIsBlank = !/[.0_*xX?·]/.test(text);
        for (i = 0; i < lines.length; i++) {
            var line = lines[i];
            if (/\+/.test(line) || /={3,}/.test(line)) continue;                      // a drawn border
            if (!dashIsBlank && /^[\s|\-]*$/.test(line)) continue;                  // a dashed border
            kept.push(line);
        }
        var body = kept.join('').replace(/[\s|,;]/g, '');
        if (!body.length) return { ok: false, error: 'empty', count: 0 };
        var values = [], bad = 0;
        for (i = 0; i < body.length; i++) {
            var ch = body.charAt(i);
            if (ch >= '1' && ch <= '9') values.push(ch.charCodeAt(0) - 48);
            else if ('0._*xX?·-'.indexOf(ch) >= 0) values.push(0);
            else bad++;
        }
        if (bad) return { ok: false, error: 'chars', count: values.length };
        var n = SIZE_BY_COUNT[values.length];
        if (!n) return { ok: false, error: 'length', count: values.length };
        for (i = 0; i < values.length; i++) {
            if (values[i] > n) return { ok: false, error: 'digit', count: values.length, n: n };
        }
        return { ok: true, n: n, values: values };
    }

    function format(values, blank) {
        blank = blank == null ? '.' : blank;
        var out = '';
        for (var i = 0; i < values.length; i++) out += values[i] ? String(values[i]) : blank;
        return out;
    }

    // Cells whose digit repeats inside a row, a column or a box.
    function conflicts(geo, values) {
        var bad = {}, out = [], u, k;
        for (u = 0; u < geo.units.length; u++) {
            var unit = geo.units[u], first = {};
            for (k = 0; k < unit.length; k++) {
                var cell = unit[k], d = values[cell];
                if (!d) continue;
                if (first[d] === undefined) first[d] = cell;
                else { bad[first[d]] = 1; bad[cell] = 1; }
            }
        }
        for (var key in bad) if (Object.prototype.hasOwnProperty.call(bad, key)) out.push(+key);
        out.sort(function (a, b) { return a - b; });
        return out;
    }

    // ── Exhaustive solver ────────────────────────────────────────────────────
    // Depth-first search over the cell with the fewest candidates, with one
    // look per node at "a digit that has a single place left in a unit" — that
    // look is what keeps sparse and broken boards from exploding. It answers a
    // different question than the logic solver: not "how would a person solve
    // it" but "how many solutions exist at all".
    //   opts.limit     stop after this many solutions (2 is enough to prove "not unique")
    //   opts.rng       GameKit.rng — try digits in a drawn order (used to deal a full grid)
    //   opts.maxNodes  give up (capped: true) instead of hanging on a pathological board
    function solve(geo, values, opts) {
        opts = opts || {};
        var n = geo.n, cells = geo.cells, full = geo.full;
        var limit = opts.limit || 2, maxNodes = opts.maxNodes || 3000000, rnd = opts.rng || null;
        var cr = geo.cellRow, cc = geo.cellCol, cb = geo.cellBox, units = geo.units;
        var rowU = new Uint16Array(n), colU = new Uint16Array(n), boxU = new Uint16Array(n);
        var grid = new Int8Array(cells), cand = new Uint16Array(cells);
        var out = { count: 0, solution: null, second: null, nodes: 0, capped: false, invalid: false };
        var i, d, bit;
        for (i = 0; i < cells; i++) {
            d = values[i] | 0;
            if (!d) continue;
            bit = 1 << (d - 1);
            if (d > n || (rowU[cr[i]] & bit) || (colU[cc[i]] & bit) || (boxU[cb[i]] & bit)) {
                out.invalid = true;
                return out;
            }
            rowU[cr[i]] |= bit; colU[cc[i]] |= bit; boxU[cb[i]] |= bit;
            grid[i] = d;
        }

        function search() {
            if (++out.nodes > maxNodes) { out.capped = true; return true; }
            var best = -1, bestMask = 0, bestCount = 99, k, m, u, j;
            for (k = 0; k < cells; k++) {
                if (grid[k]) { cand[k] = 0; continue; }
                m = full & ~(rowU[cr[k]] | colU[cc[k]] | boxU[cb[k]]);
                if (!m) return false;
                cand[k] = m;
                if (POP[m] < bestCount) { bestCount = POP[m]; best = k; bestMask = m; }
            }
            if (best < 0) {
                out.count++;
                if (out.count === 1) out.solution = Array.prototype.slice.call(grid);
                else if (out.count === 2) out.second = Array.prototype.slice.call(grid);
                return out.count >= limit;
            }
            if (bestCount > 1) {
                for (u = 0; u < units.length; u++) {
                    var unit = units[u], once = 0, twice = 0, placed = 0;
                    for (j = 0; j < n; j++) {
                        var cell = unit[j];
                        if (grid[cell]) { placed |= 1 << (grid[cell] - 1); continue; }
                        twice |= once & cand[cell];
                        once |= cand[cell];
                    }
                    var missing = full & ~placed;
                    if ((once & missing) !== missing) return false;      // a digit with no place left
                    var single = once & ~twice;
                    if (single) {
                        var sd = single & -single;
                        for (j = 0; j < n; j++) {
                            if (!grid[unit[j]] && (cand[unit[j]] & sd)) { best = unit[j]; bestMask = sd; break; }
                        }
                        break;
                    }
                }
            }
            var digits = digitsOf(bestMask);
            if (rnd && digits.length > 1) rnd.shuffle(digits);
            var r = cr[best], c = cc[best], b = cb[best];
            for (k = 0; k < digits.length; k++) {
                var bt = 1 << (digits[k] - 1);
                grid[best] = digits[k];
                rowU[r] |= bt; colU[c] |= bt; boxU[b] |= bt;
                if (search()) return true;
                grid[best] = 0;
                rowU[r] &= ~bt; colU[c] &= ~bt; boxU[b] &= ~bt;
            }
            return false;
        }

        search();
        return out;
    }

    S.POP = POP;
    S.LOW = LOW;
    S.digitsOf = digitsOf;
    S.geometry = geometry;
    S.unitKind = unitKind;
    S.unitIndex = unitIndex;
    S.sees = sees;
    S.parse = parse;
    S.format = format;
    S.conflicts = conflicts;
    S.solve = solve;
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this));
